import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Role, UserDetail, UserDetailState } from "@/types/userDetails";

import { localStorageKey } from "@/enums/localStorageKey";
import { roles } from "@/enums/roles";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@/utils/localStorageHelper";

let userDetails: UserDetail | null = null;
try {
  const details = getLocalStorageItem(localStorageKey.USER_DETAILS);
  if (details && typeof details === "string") {
    userDetails = JSON.parse(details);
  }
} catch (error) {
  if (error) console.error("Failed to fetch user Details from localhost");
}

let userRole: Role | null = null;
try {
  const role = getLocalStorageItem(localStorageKey.USER_ROLE);
  if (role) {
    if (typeof role === "string") {
      userRole = JSON.parse(role);
    } else {
      userRole = role as Role;
    }
  }
} catch (error) {
  if (error) console.error("Failed to fetch role from localhost");
}

const initialState: UserDetailState = {
  userDetail: userDetails,
  role: userRole || {
    isSuperAdmin: false,
    isBranchAdmin: false,
    isOrgAdmin: false,
  },
};

export const authSlice = createSlice({
  name: "userDetail",
  initialState,
  reducers: {
    setDetail: (state, action: PayloadAction<UserDetail>) => {
      state.userDetail = action.payload;

      setLocalStorageItem(
        localStorageKey.USER_DETAILS,
        JSON.stringify(action.payload),
      );
    },
    setRole: (state, action: PayloadAction<UserDetail>) => {
      const role: Role = {
        isSuperAdmin: false,
        isOrgAdmin: false,
        isBranchAdmin: false,
      };

      const userRole = action.payload.role;

      if (userRole === roles.SUPER_ADMIN) {
        role.isSuperAdmin = true;
      } else if (userRole === roles.ORG_ADMIN) {
        role.isOrgAdmin = true;
      } else if (userRole === roles.BRANCH_ADMIN) {
        role.isBranchAdmin = true;
      }

      state.role = role;

      setLocalStorageItem(localStorageKey.USER_ROLE, JSON.stringify(role));
    },
    updateDetails: (state, action: PayloadAction<UserDetail>) => {
      state.userDetail = {
        ...action.payload,
      };

      setLocalStorageItem(
        localStorageKey.USER_DETAILS,
        JSON.stringify(action.payload),
      );
    },
    removeDetails: (state: UserDetailState) => {
      state.userDetail = null;

      removeLocalStorageItem(localStorageKey.USER_DETAILS);
    },
  },
});

export const { setDetail, setRole, updateDetails, removeDetails } =
  authSlice.actions;

export default authSlice.reducer;
